/**
 * ==========================================================
 * Aurora Geriátrica
 * © 2026 Luis Fernando Chacón Cordón
 * Guatemala
 * Software propietario - todos los derechos reservados
 * ==========================================================
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import {
  ClipboardList,
  BarChart3,
  FileText,
  TrendingUp,
  Stethoscope,
  Check,
  ChevronDown,
  UserRound,
  Building2,
  Heart,
  ListChecks,
  Layers,
  Clock
} from "lucide-react";

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "contacto@aurorageriatrica.com";

const latamFlags = [
  { code: "gt", label: "Guatemala" },
  { code: "mx", label: "México" },
  { code: "co", label: "Colombia" },
  { code: "sv", label: "El Salvador" },
  { code: "hn", label: "Honduras" },
  { code: "cr", label: "Costa Rica" },
  { code: "pa", label: "Panamá" },
  { code: "pe", label: "Perú" },
  { code: "cl", label: "Chile" },
  { code: "ar", label: "Argentina" }
];

const navLinks = [
  { href: "#inicio", label: "Inicio" },
  { href: "#funciones", label: "Funciones" },
  { href: "#demo", label: "Demo" },
  { href: "/precios", label: "Precios" },
  { href: "/recursos", label: "Recursos" },
  { href: "#contacto", label: "Contacto" }
];

const features = [
  {
    icon: ClipboardList,
    title: "Historia clínica longitudinal",
    description: "Un solo expediente que evoluciona con cada consulta. Antecedentes, medicamentos y seguimiento longitudinal."
  },
  {
    icon: BarChart3,
    title: "Expediente geriátrico organizado",
    description: "Consultas estructuradas, agenda clínica e historial del paciente en un solo flujo de consultorio y seguimiento."
  },
  {
    icon: FileText,
    title: "Reportes clínicos profesionales",
    description: "Informes, recetas y guías para la familia. PDF descargables listos para impresión o envío."
  },
  {
    icon: TrendingUp,
    title: "Seguimiento visual y comparativo",
    description: "Compara visitas anteriores. Evolución de escalas, signos vitales y planes de cuidados en un solo lugar."
  },
  {
    icon: Stethoscope,
    title: "Diseñado exclusivamente para geriatría",
    description: "Flujo clínico pensado para el médico geriatra. No es un EMR genérico adaptado."
  },
  {
    icon: Check,
    title: "Prueba gratis 14 días",
    description: "Sin tarjeta de crédito. Accede a todas las funciones durante 14 días."
  }
];

const faqs = [
  {
    q: "¿Aurora está diseñado solo para geriatría?",
    a: "Sí. Aurora Geriátrica está pensado exclusivamente para médicos geriatras y clínicas geriátricas en Latinoamérica. El flujo clínico, el expediente y los informes están adaptados a la práctica geriátrica."
  },
  {
    q: "¿Puedo probarlo gratis?",
    a: "Sí. Ofrecemos 14 días de prueba gratuita sin necesidad de tarjeta de crédito. Tendrás acceso completo a todas las funciones."
  },
  {
    q: "¿Qué incluye el plan?",
    a: "Pacientes ilimitados, historia clínica longitudinal, consultas estructuradas, informes profesionales, seguimiento y evolución entre visitas, y más."
  },
  {
    q: "¿Puedo cancelar cuando quiera?",
    a: "Sí. Puedes cancelar en cualquier momento. No hay contratos a largo plazo."
  },
  {
    q: "¿Funciona en Latinoamérica?",
    a: "Sí. Aurora Geriátrica está diseñado para médicos en Latinoamérica. Soporta español, pesos, dólares y el flujo de consultorio habitual en la región."
  },
  {
    q: "¿Puedo usarlo desde cualquier computadora?",
    a: "Sí. Es una aplicación web. Funciona desde cualquier navegador en computadora, tableta o móvil."
  }
];

const whoFor = [
  { icon: UserRound, label: "Médicos geriatras" },
  { icon: Building2, label: "Clínicas geriátricas" },
  { icon: Stethoscope, label: "Consulta privada geriátrica" },
  { icon: Heart, label: "Residencias y centros de atención al adulto mayor" },
  { icon: ClipboardList, label: "Médicos con enfoque en adultos mayores" }
];

const landingBenefitCards = [
  {
    icon: ListChecks,
    title: "Consulta estructurada",
    description: "Motivo, antecedentes y hallazgos en un flujo guiado: menos omisiones, más claridad clínica."
  },
  {
    icon: Layers,
    title: "Consultorio y seguimiento",
    description: "Agenda clínica, consultas estructuradas e historial del paciente para continuidad de la atención."
  },
  {
    icon: FileText,
    title: "Documento clínico automático",
    description: "Informes listos para historia o entrega al paciente, sin volcar notas a otro sistema."
  },
  {
    icon: Clock,
    title: "Ahorro de tiempo real",
    description: "Menos clics y menos tareas repetitivas en cada visita; más minutos con el paciente."
  }
];

const productImageFiles = [
  "DASHBOARD.png",
  "DASHBOARD2.png",
  "escala 1.png",
  "Escalas Geriatricas.png",
  "Historia Longitudinal.png",
  "Pacientes.png",
  "Perfil Clinico.png",
  "Registro de pacientes.png",
  "Reposrtes Clinicos.png",
  "resultados automaticos 1.png",
  "Seguimiento de consulta.png",
  "login.png"
];

const flowOrder = [
  "Dashboard principal",
  "Gestión de pacientes",
  "Perfil clínico",
  "Escalas geriátricas",
  "Reportes clínicos",
  "Seguimiento de consultas"
];

const textByStage = {
  "Dashboard principal": {
    title: "Dashboard Inteligente",
    description: "Visualiza agenda, pacientes prioritarios y alertas clínicas en un panel centralizado."
  },
  "Gestión de pacientes": {
    title: "Gestión de Pacientes Ágil",
    description: "Administra pacientes de forma rápida y organizada con expedientes siempre disponibles."
  },
  "Perfil clínico": {
    title: "Perfil Clínico Unificado",
    description: "Consulta antecedentes, diagnósticos y evolución clínica en una vista clara y completa."
  },
  "Escalas geriátricas": {
    title: "Escalas Geriátricas Integradas",
    description: "Aplica escalas clave y obtén interpretaciones clínicas listas para tomar decisiones."
  },
  "Reportes clínicos": {
    title: "Informes Clínicos Automáticos",
    description: "Genera informes clínicos claros y profesionales en segundos para cada consulta."
  },
  "Seguimiento de consultas": {
    title: "Seguimiento de Consultas",
    description: "Compara visitas y asegura continuidad terapéutica con trazabilidad en todo el proceso."
  }
};

function classifyImageStage(filename) {
  const raw = filename.toLowerCase();
  const normalized = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (normalized.includes("dashboard")) return "Dashboard principal";
  if (normalized.includes("paciente") || normalized.includes("registro")) return "Gestión de pacientes";
  if (normalized.includes("perfil") || normalized.includes("historia")) return "Perfil clínico";
  if (normalized.includes("escala")) return "Escalas geriátricas";
  if (normalized.includes("report") || normalized.includes("resultados")) return "Reportes clínicos";
  if (normalized.includes("seguimiento") || normalized.includes("consulta")) return "Seguimiento de consultas";
  return "Dashboard principal";
}

const groupedImages = productImageFiles.reduce((acc, file) => {
  const stage = classifyImageStage(file);
  if (!acc[stage]) acc[stage] = [];
  acc[stage].push(file);
  return acc;
}, {});

const howItWorksBlocks = flowOrder
  .map((stage) => {
    const files = groupedImages[stage] || [];
    if (!files.length) return null;
    const copy = textByStage[stage];
    return {
      stage,
      title: copy.title,
      description: copy.description,
      image: files[0],
      allImages: files
    };
  })
  .filter(Boolean);

function LandingNav({ scrolled }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavClick = (event, link) => {
    if (!link.smooth) return;
    event.preventDefault();
    const target = document.querySelector(link.href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  const handleTrialClick = (event) => {
    event.preventDefault();
    navigate("/register");
    setOpen(false);
  };

  const handleBuyNowClick = () => {
    setOpen(false);
    startSubscriptionCheckout();
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-slate-200/80 bg-white/90 shadow-[0_8px_30px_rgb(15,23,42,0.06)] backdrop-blur-md"
          : "border-transparent bg-white/80 backdrop-blur-sm"
      }`}
    >
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[4.25rem] items-center justify-between gap-3">
          <a
            href="#inicio"
            className="group flex min-w-0 items-center text-slate-900 transition-opacity hover:opacity-90"
          >
            <Logo
              variant="wordmark"
              format="png"
              className="h-10 w-auto max-h-12 max-w-[min(62vw,260px)] object-contain object-left sm:h-12 sm:max-h-14 sm:max-w-[320px] lg:max-w-[360px]"
              alt={BRAND_NAME}
            />
          </a>

          <div className="hidden items-center gap-1 md:flex md:gap-0.5 lg:gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(event) => handleNavClick(event, link)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
              >
                {link.label}
              </a>
            ))}
            <a href="/register" onClick={handleTrialClick} className="ml-3 pl-2 lg:ml-5">
              <Button className="h-10 rounded-xl bg-sky-600 px-5 text-sm font-semibold text-white shadow-sm shadow-sky-600/25 ring-1 ring-sky-600/20 transition-all hover:bg-sky-700 hover:shadow-md">
                Probar gratis
              </Button>
            </a>
            <Button
              className="h-10 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-slate-800"
              onClick={handleBuyNowClick}
            >
              Comprar ahora
            </Button>
          </div>

          <div className="md:hidden">
            <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setOpen(!open)} aria-label="Menú">
              <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {open ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </Button>
            {open && (
              <div className="absolute left-0 right-0 top-[4.25rem] flex flex-col gap-0.5 border-b border-slate-100 bg-white/98 py-4 shadow-lg backdrop-blur-md">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(event) => {
                      handleNavClick(event, link);
                      if (!link.smooth) setOpen(false);
                    }}
                    className="px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    {link.label}
                  </a>
                ))}
                <a href="/register" onClick={handleTrialClick} className="px-4 pt-3">
                  <Button className="h-11 w-full rounded-xl bg-sky-600 text-sm font-semibold hover:bg-sky-700">Probar gratis</Button>
                </a>
                <div className="px-4 pt-2">
                  <Button
                    className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800"
                    onClick={handleBuyNowClick}
                  >
                    Comprar ahora
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [visibleHowItWorks, setVisibleHowItWorks] = useState({});

  const handleTrialClick = (event) => {
    event.preventDefault();
    navigate("/register");
  };

  const handleBuyNowHeroClick = () => {
    console.log("[checkout-click]", "landing-hero");
    startSubscriptionCheckout();
  };

  const handleBuyNowMidCtaClick = () => {
    console.log("[checkout-click]", "landing-cta-intermedio");
    startSubscriptionCheckout();
  };

  const handleBuyNowPricingCardClick = () => {
    console.log("[checkout-click]", "landing-card-precios");
    startSubscriptionCheckout();
  };

  const handleBuyNowBottomCtaClick = () => {
    console.log("[checkout-click]", "landing-cta-inferior-azul");
    startSubscriptionCheckout();
  };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const key = entry.target.getAttribute("data-how-step");
          if (entry.isIntersecting && key) {
            setVisibleHowItWorks((prev) => ({ ...prev, [key]: true }));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    const blocks = document.querySelectorAll("[data-how-step]");
    blocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-landing="aurora">
      <LandingNav scrolled={scrolled} />

      {/* Hero */}
      <section
        id="inicio"
        className="border-b border-slate-100/80 bg-gradient-to-b from-white via-white to-slate-50/60 px-4 pb-20 pt-[5.5rem] sm:px-6 sm:pb-24 sm:pt-32 lg:px-8"
      >
        <div className="mx-auto max-w-6xl">
          <div className="hero grid items-start gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-center lg:gap-14">
            <div className="w-full max-w-xl">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                Consulta geriátrica digital · Latinoamérica
              </p>
              <h1 className="max-w-xl text-3xl font-semibold leading-tight tracking-tight text-slate-950 lg:text-5xl lg:leading-[1.08]">
                La consulta geriátrica digital completa — historia clínica, seguimiento longitudinal e informes PDF
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600 lg:text-xl">
                Diseñado para médicos geriatras y médicos generales de Latinoamérica. Prueba gratis por 14 días, sin
                tarjeta.
              </p>
              <div className="mt-6 max-w-xl rounded-3xl border border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-slate-50 px-5 py-5 shadow-[0_16px_40px_rgba(14,165,233,0.10)] sm:px-6 sm:py-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">Prueba gratuita</p>
                <h2 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">14 días gratis</h2>
                <p className="mt-1 text-lg font-semibold text-slate-700 sm:text-xl">Sin tarjeta de crédito</p>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
                  Historia clínica, examen físico, seguimiento longitudinal e informes clínicos personalizables desde el primer día.
                </p>
                <a href="/register" onClick={handleTrialClick} className="mt-5 inline-flex w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="h-11 w-full rounded-xl bg-sky-600 px-7 text-sm font-semibold text-white shadow-md shadow-sky-600/20 transition-all hover:bg-sky-700 sm:w-auto"
                  >
                    Comenzar prueba gratis
                  </Button>
                </a>
              </div>
              <div className="mt-5 flex max-w-xl flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="w-full text-sm font-semibold text-slate-800">Disponible para médicos en Latinoamérica</p>
                <div className="flex flex-wrap items-center gap-2">
                  {latamFlags.map((flag) => (
                    <img
                      key={flag.code}
                      src={`https://flagcdn.com/w40/${flag.code}.png`}
                      alt={flag.label}
                      width={28}
                      height={20}
                      loading="lazy"
                      decoding="async"
                      className="h-5 w-7 rounded-sm object-cover shadow-sm ring-1 ring-slate-200/80"
                    />
                  ))}
                </div>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600">
                Herramienta especializada para consulta geriátrica — no un EMR genérico adaptado a medias.
              </p>
              <div className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row sm:items-center">
                <a href="/register" onClick={handleTrialClick} className="sm:w-auto">
                  <Button
                    size="lg"
                    className="h-12 w-full rounded-xl bg-sky-600 px-8 text-base font-semibold text-white shadow-md transition-all hover:bg-sky-700 sm:w-auto"
                  >
                    Empieza tu prueba gratis
                  </Button>
                </a>
                <a href="/demo-clinico" className="sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 w-full rounded-xl border border-slate-300 bg-white px-8 text-base font-semibold text-slate-800 transition-all hover:bg-slate-50 sm:w-auto"
                  >
                    Ver demo en vivo
                  </Button>
                </a>
              </div>
              <p className="mt-3 text-center text-sm text-slate-500 sm:text-left">
                Sin tarjeta · Acceso inmediato · Prueba completa por 14 días
              </p>
              <div className="mt-6 max-w-xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Empieza en 3 pasos</p>
                <ol className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-3">
                  {[
                    { n: 1, t: "Crea tu cuenta" },
                    { n: 2, t: "Prueba una evaluación" },
                    { n: 3, t: "Genera tu informe" }
                  ].map((step) => (
                    <li key={step.n} className="flex flex-1 items-center gap-3 sm:min-w-0">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">
                        {step.n}
                      </span>
                      <span className="text-sm font-medium text-slate-700">{step.t}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl min-w-0 lg:mx-0">
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 shadow-2xl">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="truncate text-xs font-medium text-slate-600">Vista rápida de Aurora</span>
                  <span className="shrink-0 text-xs font-medium text-slate-500">Dashboard clínico</span>
                </div>
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="mt-2 aspect-video w-full rounded-xl bg-slate-100 object-cover"
                  aria-label="Panel de Aurora Geriátrica"
                >
                  <source src="/videodashboard.mp4" type="video/mp4" />
                </video>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {["Escalas", "Alertas", "PDF clínico"].map((label) => (
                    <span
                      key={label}
                      className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios — valor rápido */}
      <section
        className="bg-[#fafbfc] px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
        aria-label="Beneficios principales"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Pensado para la consulta geriátrica real
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
              Lo esencial que un doctor necesita ver en segundos: estructura, escalas, documentación y tiempo.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {landingBenefitCards.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group flex min-h-[220px] flex-col rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-sky-200/60 hover:shadow-[0_12px_40px_rgba(14,165,233,0.08)]"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-slate-50 text-sky-700 ring-1 ring-sky-100/80 shadow-sm">
                  <Icon className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
                </div>
                <h3 className="mt-5 text-base font-semibold leading-snug tracking-tight text-slate-900">{title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{description}</p>
              </div>
            ))}
          </div>
          <div className="mx-auto mt-12 max-w-2xl rounded-2xl border border-slate-200 bg-white px-5 py-6 text-center shadow-sm sm:px-8">
            <p className="text-base font-semibold text-slate-900">¿Prefieres activar Aurora directamente?</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Puedes iniciar tu suscripción cuando estés listo. La prueba gratis sigue disponible sin tarjeta.
            </p>
            <Button
              className="mt-5 h-10 rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white hover:bg-slate-800"
              onClick={handleBuyNowHeroClick}
            >
              Comprar ahora
            </Button>
          </div>
        </div>
      </section>

      {/* Problem / Value */}
      <section className="bg-white px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Por qué la geriatría exige una herramienta propia
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-600 sm:text-base">
            El adulto mayor no se valora como un episodio aislado: hace falta continuidad, escalas y documentos que
            hablen entre sí.
          </p>
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:gap-10">
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-8 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">El dolor en consulta</p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">Los EMR genéricos le roban tiempo al médico</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Formularios largos, poca guía geriátrica y herramientas de texto sueltas obligan a repetir trabajo: copiar
                escalas, rearmar informes y perder el hilo entre visitas. La geriatría integrada no cabe en un flujo pensado
                para urgencias o consulta general.
              </p>
            </div>
            <div className="rounded-2xl border border-sky-200/60 bg-gradient-to-b from-sky-50/80 to-white p-8 shadow-sm ring-1 ring-sky-100/50">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-800/80">Cómo Aurora ayuda</p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">Un solo lugar para valorar, documentar y seguir</h3>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-slate-700">
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  Consulta guiada y expediente longitudinal
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  Escalas y resultados en el mismo expediente
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  Informes clínicos listos sin reescribir notas
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  Comparación entre visitas para ver evolución
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  Materiales claros para compartir con la familia
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core features */}
      <section id="funciones" className="bg-slate-50/60 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Funciones principales
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-600 sm:text-base">
            Historia longitudinal, escalas, informes y seguimiento — integrados para el médico geriatra.
          </p>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-300 hover:border-slate-200 hover:shadow-md"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 ring-1 ring-sky-100/80">
                    <Icon className="h-5 w-5 text-sky-700" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Preview - Screenshots */}
      <section id="como-funciona" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center mb-16">
            <Logo variant="wordmark" format="png" size="lg" className="mb-4 opacity-95" />
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
              Todo lo que necesitas para gestionar pacientes geriátricos en un solo lugar
            </h2>
            <p className="mt-3 text-slate-600 text-center max-w-2xl mx-auto">
              Desde el registro hasta los informes clínicos, Aurora te acompaña en cada paso.
            </p>
          </div>
          <div className="space-y-10 sm:space-y-12">
            {howItWorksBlocks.map((block, index) => {
              const imageLeft = index % 2 === 0;
              const isVisible = Boolean(visibleHowItWorks[block.stage]);
              return (
                <article
                  key={block.stage}
                  data-how-step={block.stage}
                  className={`rounded-3xl border border-slate-200 bg-white shadow-sm hover:shadow-xl transition-all duration-700 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                  }`}
                >
                  <div className={`grid lg:grid-cols-2 gap-0 ${imageLeft ? "" : "lg:[&>*:first-child]:order-2"}`}>
                    <div className="p-5 sm:p-8 lg:p-10 bg-slate-50/60 rounded-t-3xl lg:rounded-l-3xl lg:rounded-tr-none">
                      <div className="aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
                        <img
                          src={encodeURI(`/images/${block.image}`)}
                          alt={block.title}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center text-center lg:text-left">
                      <p className="text-xs uppercase tracking-[0.16em] text-sky-600 font-semibold">
                        {String(index + 1).padStart(2, "0")} · {block.stage}
                      </p>
                      <h3 className="mt-2 text-2xl sm:text-[1.75rem] leading-tight font-bold text-slate-900">
                        {block.title}
                      </h3>
                      <p className="mt-3 text-slate-600 max-w-lg">
                        {block.description}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="mt-14 rounded-3xl border border-sky-200/60 bg-gradient-to-br from-sky-50/90 via-white to-slate-50 p-8 text-center shadow-sm sm:mt-16 sm:p-12">
            <h3 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              ¿Listo para ordenar su consulta geriátrica?
            </h3>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Pruebe el flujo completo con su propia rutina: pacientes, escalas e informes en un solo entorno clínico.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <a href="/register" onClick={handleTrialClick}>
                <Button
                  size="lg"
                  className="h-11 rounded-xl bg-sky-600 px-8 text-sm font-semibold text-white shadow-md shadow-sky-600/20 hover:bg-sky-700"
                >
                  Probar gratis
                </Button>
              </a>
              <Button
                size="lg"
                variant="outline"
                className="h-11 rounded-xl border-slate-300 px-8 text-sm font-semibold"
                onClick={handleBuyNowMidCtaClick}
              >
                Comprar ahora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
            Demostración de Aurora Geriátrica
          </h2>
          <p className="mt-3 text-slate-600 text-center max-w-xl mx-auto">
            Mira cómo funciona la plataforma en unos minutos
          </p>
          <div className="mt-10 flex justify-center w-full">
            <video
              controls
              playsInline
              preload="metadata"
              className="rounded-xl shadow-lg w-full h-auto max-w-full"
            >
              <source src="/Demo2026.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
            Para quién es Aurora Geriátrica
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {whoFor.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <Icon className="h-5 w-5 text-sky-600" />
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Medical resources / sponsors */}
      <section id="recursos" className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-semibold text-slate-800 text-center">
            Recursos y partners médicos
          </h2>
          <p className="mt-2 text-sm text-slate-500 text-center max-w-xl mx-auto">
            Productos y servicios relevantes para la práctica geriátrica
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "Equipos de movilidad",
              "Suplementos nutricionales",
              "Equipamiento médico",
              "Cursos y congresos",
              "Laboratorios y diagnósticos"
            ].map((label) => (
              <div
                key={label}
                className="h-24 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 text-sm"
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precio" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
            Precio simple y transparente
          </h2>
          <div className="mt-10 p-8 rounded-2xl border-2 border-sky-200 bg-white shadow-lg">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-800">Aurora Geriátrica Profesional</h3>
              <p className="mt-2 text-4xl font-bold text-slate-900">$39 <span className="text-lg font-normal text-slate-500">USD / mes</span></p>
              <p className="mt-1 text-sm text-slate-500">1 perfil médico · 14 días gratis</p>
              <ul className="mt-6 text-left space-y-3 text-slate-700">
                {[
                  "Pacientes ilimitados",
                  "Historia clínica longitudinal",
                  "Consultas estructuradas e historial",
                  "Reportes profesionales",
                  "Seguimiento y evolución"
                ].map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-sky-600 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="/register"
                  onClick={handleTrialClick}
                  className="flex-1"
                >
                  <Button size="lg" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    Comenzar prueba gratuita
                  </Button>
                </a>
                <Button size="lg" variant="outline" className="flex-1 border-slate-300" onClick={handleBuyNowPricingCardClick}>
                  Comprar ahora
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50/50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center">
            Preguntas frecuentes
          </h2>
          <div className="mt-10 space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-lg bg-white border border-slate-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left font-medium text-slate-800 hover:bg-slate-50 transition-colors"
                >
                  {faq.q}
                  <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-sm text-slate-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-3xl overflow-hidden rounded-3xl border border-sky-700/20 bg-gradient-to-br from-sky-800 via-sky-800 to-slate-900 px-6 py-14 text-center shadow-[0_24px_60px_rgba(14,116,144,0.35)] sm:px-12">
          <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Pruebe Aurora con calma: 14 días, sin tarjeta
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-sky-100/95 sm:text-base">
            Acceso completo para evaluar el flujo clínico, las escalas y los informes en su propia práctica. Sin
            compromiso ni fricción administrativa.
          </p>
          <ul className="mx-auto mt-8 flex flex-col items-center gap-2 text-left text-sm text-sky-100/90 sm:flex-row sm:justify-center sm:gap-8 sm:text-center">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
              Registro en minutos
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
              Misma experiencia que el plan pago
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
              Cancele cuando quiera
            </li>
          </ul>
          <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <a href="/register" onClick={handleTrialClick}>
              <Button
                size="lg"
                className="h-12 w-full min-w-[200px] rounded-xl bg-white px-8 text-base font-semibold text-sky-800 shadow-lg shadow-slate-900/20 transition-all hover:bg-sky-50 sm:w-auto"
              >
                Probar gratis
              </Button>
            </a>
            <Button
              size="lg"
              className="h-12 w-full rounded-xl bg-white/10 px-8 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/15 sm:w-auto"
              onClick={handleBuyNowBottomCtaClick}
            >
              Comprar ahora
            </Button>
            <a href="#demo">
              <Button
                size="lg"
                variant="outline"
                className="h-12 w-full rounded-xl border-white/40 bg-white/5 px-8 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/10 sm:w-auto"
              >
                Ver demostración
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer + Contact section */}
      <footer id="contacto" className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-slate-300">
        <div className="max-w-6xl mx-auto">
          {/* Configurable contact section */}
          <div className="mb-10 pb-10 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contacto</h3>
            <div className="flex flex-wrap gap-6 items-center">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                {CONTACT_EMAIL}
              </a>
              <a
                href="/contacto"
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Página de contacto
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Publicidad y anuncios`}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Publicidad / Anunciantes
              </a>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-2 text-white font-semibold">
              <Logo variant="icon" size="md" className="opacity-90" />
              {BRAND_NAME}
            </div>
            <div className="flex flex-wrap gap-6">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="text-sm hover:text-white transition-colors">
                  {link.label}
                </a>
              ))}
              <a href="/privacidad" className="text-sm hover:text-white transition-colors">Privacidad</a>
              <a href="/terminos" className="text-sm hover:text-white transition-colors">Términos</a>
              <a href="/contacto" className="text-sm hover:text-white transition-colors">Contacto</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col gap-4">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} {BRAND_NAME} — Desarrollado por Luis Chacón. Todos los derechos reservados.
            </p>
            <p className="text-xs text-slate-500 max-w-3xl leading-relaxed">
              Aurora Geriátrica es una herramienta de apoyo clínico y no sustituye el juicio médico
              profesional. Las decisiones médicas son responsabilidad exclusiva del profesional de salud.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
